import React, { useEffect, useState, useRef } from 'react';
import { Zap, Radiation } from 'lucide-react';
import { AUDIO_PATHS } from '../config/gameConstants';

/**
 * EnrichmentPurchasePopup - Shows when a country purchases uranium enrichment
 * Features: Country flag, radiation symbols, cost display, cash register sound
 */
export default function EnrichmentPurchasePopup({ 
  countryCode, 
  countryName, 
  enrichmentGained, 
  cost, 
  newEnrichment,
  onComplete 
}) {
  const [visible, setVisible] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Show popup
    setVisible(true);

    // Play cash register sound
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(err => console.warn('Cash register audio failed:', err));
    }

    // Auto-hide after 3 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        onComplete && onComplete();
      }, 500);
    }, 3000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Country flag emojis
  const FLAG_EMOJIS = {
    'USA': '🇺🇸',
    'RUS': '🇷🇺',
    'CHN': '🇨🇳',
    'EU': '🇪🇺',
    'NK': '🇰🇵'
  };

  const flag = FLAG_EMOJIS[countryCode] || '🏳️';

  return (
    <>
      {/* Cash register audio */}
      <audio ref={audioRef} src={AUDIO_PATHS.cashRegister} preload="auto" />

      {/* Popup overlay */}
      <div 
        className={`fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none transition-opacity duration-500 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Background blur */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

        {/* Popup card */}
        <div 
          className={`relative bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 border-4 border-purple-400 rounded-2xl p-8 shadow-2xl transform transition-all duration-500 ${
            visible ? 'scale-100 rotate-0' : 'scale-50 rotate-12'
          }`}
          style={{
            boxShadow: '0 0 60px rgba(168, 85, 247, 0.8), 0 0 120px rgba(168, 85, 247, 0.4)',
            minWidth: '400px'
          }}
        >
          {/* Radiation symbols floating around */}
          <div className="absolute -top-4 -left-4 text-6xl animate-pulse">
            ☢️
          </div>
          <div className="absolute -top-4 -right-4 text-6xl animate-pulse" style={{ animationDelay: '0.3s' }}>
            ☢️
          </div>
          <div className="absolute -bottom-4 -left-4 text-6xl animate-pulse" style={{ animationDelay: '0.6s' }}>
            ☢️
          </div>
          <div className="absolute -bottom-4 -right-4 text-6xl animate-pulse" style={{ animationDelay: '0.9s' }}>
            ☢️
          </div>

          {/* Lightning bolts */}
          <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
            <Zap className="w-12 h-12 text-yellow-400 animate-pulse" />
          </div>
          <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
            <Zap className="w-12 h-12 text-yellow-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>

          {/* Content */}
          <div className="relative z-10 text-center space-y-4">
            {/* Flag and country name */}
            <div className="flex items-center justify-center gap-3">
              <span className="text-7xl">{flag}</span>
              <div>
                <div className="text-3xl font-bold text-white">{countryName}</div>
                <div className="text-sm text-purple-300 uppercase tracking-wider">Enrichment Purchase</div>
              </div>
            </div>

            {/* Enrichment gained */}
            <div className="bg-purple-950/50 rounded-xl p-4 border-2 border-purple-400">
              <div className="flex items-center justify-center gap-3">
                <Radiation className="w-8 h-8 text-yellow-400" />
                <div className="text-5xl font-bold text-yellow-400">
                  +{enrichmentGained}%
                </div>
                <Radiation className="w-8 h-8 text-yellow-400" />
              </div>
              <div className="text-sm text-purple-200 mt-2">
                Uranium Enrichment
              </div>
            </div>

            {/* Cost */}
            <div className="text-xl text-green-400 font-bold">
              💰 Cost: ${cost}M
            </div>

            {/* New total */}
            <div className="text-lg text-purple-300">
              New Total: <span className="text-yellow-400 font-bold">{newEnrichment}%</span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-800 h-4 rounded-full overflow-hidden border-2 border-purple-500">
              <div 
                className="h-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 transition-all duration-1000"
                style={{ width: `${Math.min(100, newEnrichment)}%` }}
              />
            </div>
          </div>

          {/* Glowing particles effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            {[...Array(20)].map((_, i) => (
              <div
                key={`particle-enrichment-${i}`}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full opacity-60"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `float-particle ${2 + Math.random() * 3}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* CSS for particle animation */}
      <style jsx>{`
        @keyframes float-particle {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.6;
          }
          50% {
            transform: translate(${Math.random() * 40 - 20}px, ${Math.random() * 40 - 20}px) scale(1.5);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
